import { useId } from "react";
import { Link, type To } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import type { CustomerCatalogueItemSummary } from "@shared-core/data/customer-api-types";
import {
  ProductRender,
  renderKindFor,
} from "../../customer-pages/ProductRender";

// Cross-sell cards, one component for both rows the page can show: "Complete
// the set" (the item's curated related items) and, when it has none, "More from
// your catalogue" (the other items in this customer's own catalogue). "From" is
// the price the item summary already gives for its smallest quantity, never a
// second computation.
export function ProductCrossSell({
  heading,
  items,
  buildPortalUrl,
}: {
  heading: string;
  items: CustomerCatalogueItemSummary[];
  buildPortalUrl: (path: string) => To;
}) {
  const headingId = useId();
  if (items.length === 0) return null;
  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <h2 id={headingId} className="text-lg font-semibold">
        {heading}
      </h2>
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((item) => (
          <li key={item.item_id}>
            <Link
              to={buildPortalUrl(
                paths.portalCatalogueItem({ itemId: item.item_id }),
              )}
              className="group flex flex-col overflow-hidden rounded-xl border bg-card outline-none transition-shadow hover:border-ring/60 hover:shadow-md focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <div className="aspect-[4/3] w-full bg-muted/40">
                <ProductRender kind={renderKindFor(item)} />
              </div>
              <div className="space-y-0.5 p-4">
                <p className="leading-snug font-medium group-hover:text-primary">
                  {item.name}
                </p>
                {item.quoted_to_order ? (
                  <p className="text-sm text-muted-foreground">
                    Quoted to order
                  </p>
                ) : item.price ? (
                  <p className="text-sm text-muted-foreground tabular-nums">
                    From {formatQuotedPrice(item.price.price_pence)}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Price on request
                  </p>
                )}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
